import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import { LanguageProvider } from './contexts/LanguageContext';
import LocalizedRouter from './components/LocalizedRouter';

// Eager load HomePage for better initial load
import HomePage from './pages/HomePage';

// Lazy load other pages for code splitting (Requirement 12.4)
const NewsPage = lazy(() => import('./pages/NewsPage'));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage'));
const ActivitiesPage = lazy(() => import('./pages/ActivitiesPage'));
const ActivityDetailPage = lazy(() => import('./pages/ActivityDetailPage'));
const ConferenceListingPage = lazy(() => import('./pages/ConferenceListingPage'));
const ConferencePage = lazy(() => import('./pages/ConferencePage'));
const AwardPage = lazy(() => import('./pages/AwardPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ArticleCardTestPage = lazy(() => import('./pages/ArticleCardTestPage'));
const ArticleListDemo = lazy(() => import('./pages/ArticleListDemo'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="large" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <LanguageProvider>
          <LocalizedRouter>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Root redirect handled by LocalizedRouter */}
                  <Route path="/" element={<HomePage />} />
                  
                  {/* Localized routes with /:lang prefix */}
                  <Route path="/:lang" element={<HomePage />} />
                  <Route path="/:lang/news" element={<NewsPage />} />
                  <Route path="/:lang/news/:slug" element={<ArticleDetailPage />} />
                  <Route path="/:lang/activities" element={<ActivitiesPage />} />
                  <Route path="/:lang/activities/:slug" element={<ActivityDetailPage />} />
                  <Route path="/:lang/research" element={<Navigate to="../activities" replace />} />
                  <Route path="/:lang/conferences" element={<ConferenceListingPage />} />
                  <Route path="/:lang/conferences/:slug" element={<ConferencePage />} />
                  <Route path="/:lang/awards" element={<AwardPage />} />
                  <Route path="/:lang/about" element={<AboutPage />} />
                  <Route 
                    path="/:lang/contact" 
                    element={<Navigate to="../about#contact" replace />} 
                  />
                  <Route path="/:lang/search" element={<SearchPage />} />
                  
                  {/* Test pages (development only) */}
                  <Route path="/:lang/test/article-card" element={<ArticleCardTestPage />} />
                  <Route path="/:lang/test/article-list" element={<ArticleListDemo />} />
                  
                  {/* 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </Layout>
          </LocalizedRouter>
        </LanguageProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
